import {
  bulkDeleteSalesTaxProducts,
  bulkExportProducts,
  bulkUpdateSalesTaxProducts,
  useCorporationSettings,
} from '@commenda/api';
import {
  ExportDataDeliveryType,
  ICommendaProduct,
} from '@commenda/api/new-sales-tax/products/types';
import { TransactionSourcePlatformText } from '@commenda/api/sales-tax/constants';
import { getClientInfoFromCookie } from '@commenda/auth';
import { Badge, Button, DataTable, useToggle } from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { KeyedMutator } from 'swr';
import { ProductFilterState } from '../common/filters/types';
import SalesTaxSectionLayout from '../common/section-layout';
import { useTableRowSelectionHandler } from '../common/utils/useTableRowSelectionHandler';
import DeleteBulkProductsModal from './modal/delete-bulk-products';
import UpdateTaxCodeModal from './modal/update-tax-code-modal';

interface ProductsTableSectionProps {
  corporationId: number;
  data: ICommendaProduct[];
  filters: ProductFilterState;
  pageSize: number;
  totalProducts: number;
  nextCursor: string | null;
  currentCursor: string | null;
  onPageChange: (cursor: string | null) => void;
  mutate: KeyedMutator<any>;
  searchTerm: string;
  onSearch: (searchValue: string) => void;
  setNotification: TSetNotification;
  resetCursor: () => void;
  clearFilters: () => void;
}

const ProductsTableSection = ({
  data,
  corporationId,
  pageSize,
  nextCursor,
  currentCursor,
  onPageChange,
  mutate,
  totalProducts,
  filters,
  searchTerm,
  onSearch,
  setNotification,
  resetCursor,
  clearFilters,
}: ProductsTableSectionProps) => {
  const [visibleSelectedRows, setVisibleSelectedRows] = useState<
    ICommendaProduct[]
  >([]);
  const router = useRouter();
  const clientInfo = getClientInfoFromCookie();
  const user = clientInfo?.user;
  const FIRST_PAGE_CURSOR = 'FIRST_PAGE_CURSOR';

  const { data: corporationSettings } = useCorporationSettings({
    corporationId,
  });

  const updateTaxCodeModal = useToggle();
  const deleteBulkModal = useToggle();
  const productSelection = useTableRowSelectionHandler({
    getItemId: (item: ICommendaProduct): string => item.id,
    totalItems: totalProducts,
  });

  const getProductSelectionFilters = () => {
    const selectedRowsId = Array.from(productSelection.selectedItemIds);
    const unselectedRowsId = Array.from(productSelection.unselectedItemIds);

    return {
      corporation_id: corporationId,
      // If specific products are selected, use their IDs
      ...(selectedRowsId.length > 0 ? { product_ids: selectedRowsId } : {}),
      // If in "select all" mode, use excluded_ids
      ...(!selectedRowsId.length ? { excluded_ids: unselectedRowsId } : {}),
      // Include any active filters
      ...(searchTerm && { search_term: searchTerm }),
      ...(filters.tax_codes && { tax_codes: filters.tax_codes }),
    };
  };

  const handleTaxCodeUpdate = async (taxCode: string) => {
    try {
      const response = await bulkUpdateSalesTaxProducts(
        {
          update: { tax_code: taxCode },
          filters: getProductSelectionFilters(),
        },
        undefined
      );

      if (response.success) {
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message: 'Tax codes updated successfully',
        });
        mutate();
        productSelection.resetSelection();
        clearFilters();
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: response.error || 'Failed to update tax codes',
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update tax codes',
      });
    } finally {
      updateTaxCodeModal.hide();
    }
  };

  // Keep track of visible selected rows for the current page
  useEffect(() => {
    const currentPageSelected = data.filter((item) =>
      productSelection.isItemSelected(item)
    );
    setVisibleSelectedRows(currentPageSelected);
  }, [
    data,
    productSelection.selectedItemIds,
    productSelection.unselectedItemIds,
    productSelection.isSelectAllActive,
  ]);

  const defaultVisibleColumns = [
    'sku',
    'name',
    'description',
    'taxCode',
    'sourcePlatform',
  ];

  const SALES_TAX_PRODUCTS_TABLE_COLUMN_GROUP_STORAGE_KEY = `sales-tax-products-table-column-group-corporation-${corporationId}`;

  const handleNextPage = () => {
    if (nextCursor) {
      onPageChange(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    onPageChange(currentCursor);
  };

  const columns = [
    {
      accessorKey: 'sku',
      title: 'SKU',
      header: 'SKU',
      cell: (row) => {
        const value = row.getValue('sku');
        return (
          <div className="overflow-hidden">
            <p className="truncate">{value || '—'}</p>
          </div>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Product name',
      title: 'Product name',
      width: 200,
      cell: (row) => {
        const value = row.getValue('name');
        return (
          <div className="overflow-hidden">
            <p className="truncate">{value || '—'}</p>
          </div>
        );
      },
    },
    {
      accessorKey: 'taxCode',
      header: 'Product tax code',
      title: 'Product tax code',
      cell: ({ row }) => {
        const corpDefaultTaxCode = corporationSettings?.data?.find(
          (setting) => setting?.state === 'COUNTRYWIDE'
        )?.data?.default_product_taxability_code;

        const taxCode = row.getValue('taxCode');

        return taxCode && taxCode !== '' ? (
          <Badge variant="gray">
            <p>{taxCode.replaceAll('_', ' ')}</p>
          </Badge>
        ) : corpDefaultTaxCode ? (
          <Badge variant="blue">
            Default – {corpDefaultTaxCode.replaceAll('_', ' ')}
          </Badge>
        ) : (
          '-'
        );
      },
    },
    {
      accessorKey: 'sourcePlatform',
      header: 'Source',
      title: 'Source',
      width: 200,
      cell: ({ row }) => {
        const value = row.getValue('sourcePlatform');
        if (!value) return <Badge variant="gray">Manual upload</Badge>;
        return (
          <Badge variant="gray">{TransactionSourcePlatformText[value]}</Badge>
        );
      },
    },
    {
      accessorKey: 'sourcePlatformId',
      header: 'Source ID',
      title: 'Source ID',
      cell: ({ row }) => {
        const value = row.getValue('sourcePlatformId');
        return <Badge variant="gray">{value || '—'}</Badge>;
      },
    },
    {
      accessorKey: 'description',
      header: 'Product description',
      title: 'Product description',
      width: 250,
      cell: (row) => {
        const value = row.getValue('description');
        return (
          <div className="overflow-hidden">
            <p className="truncate">{value || '—'}</p>
          </div>
        );
      },
    },
  ];

  const columnGroups = columns.map((column) => ({
    id: column.accessorKey,
    columns: [column],
  }));

  const handleProductRowClick = (row: any) => {
    router.push(`/sales-tax/product/${row.id}?corporationId=${corporationId}`);
  };

  const getEmptyStateConfig = () => {
    const hasActiveFilters =
      data.length === 0 &&
      Object.keys(filters || {}).some(
        (key) => filters[key] && key !== 'corporationId'
      );

    if (hasActiveFilters) {
      return {
        title: 'No products found',
        description: (
          <>
            We couldn't find any products matching your current filters. Review
            your <br />
            selections, or reset filters to view all products.
          </>
        ),
      };
    }
    return {
      title: 'No products found',
      description: (
        <>
          There are no products to display at this time. Try refining <br />
          your search term, adjusting the filters, or reset both to see all
          products.
        </>
      ),
    };
  };

  const getInitialVisibleColumns = () => {
    const storageKey = SALES_TAX_PRODUCTS_TABLE_COLUMN_GROUP_STORAGE_KEY;
    const storedColumns = localStorage.getItem(storageKey);
    return storedColumns ? JSON.parse(storedColumns) : defaultVisibleColumns;
  };

  const handleBulkDelete = async () => {
    try {
      const response = await bulkDeleteSalesTaxProducts(
        {
          filters: getProductSelectionFilters(),
        },
        undefined
      );

      if (response.success) {
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message: 'Products deleted successfully',
        });
        mutate();
        productSelection.resetSelection();
        clearFilters();
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: response.error || 'Failed to delete products',
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to delete products',
      });
    } finally {
      deleteBulkModal.hide();
    }
  };

  const handleBulkExport = async () => {
    try {
      const response = await bulkExportProducts({
        filters: getProductSelectionFilters(),
        delivery: {
          type: ExportDataDeliveryType.EMAIL,
          recipients: [user.email],
        },
      });

      if (response.success) {
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message:
            'Product export in progress. You will receive an email with the export once it is ready.',
        });
        mutate();
        productSelection.resetSelection();
        resetCursor();
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: response.error || 'Failed to export products',
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to export products',
      });
    }
  };

  return (
    <SalesTaxSectionLayout>
      <DeleteBulkProductsModal
        isOpen={deleteBulkModal.visible}
        onClose={deleteBulkModal.hide}
        onDelete={handleBulkDelete}
        totalSelected={productSelection.totalSelected}
      />
      <UpdateTaxCodeModal
        isOpen={updateTaxCodeModal.visible}
        onClose={updateTaxCodeModal.hide}
        onUpdate={handleTaxCodeUpdate}
        totalSelected={productSelection.totalSelected}
        corporationId={corporationId}
      />
      <DataTable
        onRowClick={(row) => {
          handleProductRowClick(row);
        }}
        columnGroups={columnGroups}
        data={data}
        paginationConfig={{
          isEnabled: true,
          pageSize,
          type: 'CURSOR',
          hasNextPage: !!nextCursor,
          onNextPage: handleNextPage,
          onPreviousPage: handlePreviousPage,
          hasPreviousPage:
            !!currentCursor && currentCursor !== FIRST_PAGE_CURSOR,
        }}
        searchConfig={{
          isEnabled: true,
          searchValue: searchTerm,
          columnsToEnableSearchFor: ['name', 'sku', 'description'],
          placeholder: 'Search name, sku, or description',
          onSearch: (searchValue) => {
            onSearch(searchValue);
          },
        }}
        columnSelectorConfig={{
          isEnabled: true,
          placeholder: 'Customise columns',
          searchText: 'Search columns',
          notFoundText: 'No columns found',
          selectAllText: 'Show all columns',
          defaultVisibleColumns: getInitialVisibleColumns(),
          onColumnVisibilityChange: (visibleColumns) => {
            localStorage.setItem(
              SALES_TAX_PRODUCTS_TABLE_COLUMN_GROUP_STORAGE_KEY,
              JSON.stringify(visibleColumns)
            );
          },
        }}
        rowSelectionConfig={{
          isEnabled: true,
          selectionMode: 'multiple',
          shouldShowRowSelectActionMenu: true,
          isRowSelected: (row) => productSelection.isItemSelected(row),
          initialSelectedRows: visibleSelectedRows.map((row) => row.id),
          onSelectionChange: (rows) => {
            if (rows.length === data.length) {
              productSelection.handleSelectPage(data, true);
            } else if (rows.length === 0) {
              productSelection.handleSelectPage(data, false);
              if (
                productSelection.selectedItemIds.size == 0 &&
                !productSelection.isSelectAllActive
              ) {
                productSelection.resetSelection();
              }
            } else {
              productSelection.handleSelectPage(data, false);
              rows.forEach((row) =>
                productSelection.handleSelectItem(row, true)
              );
            }
          },
          getRowId: (row: any) => row.id,
          rowSelectActionMenu: () => (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <p className="txt-small-medium text-white">
                  {productSelection.totalSelected} products selected
                </p>

                {!productSelection.isSelectAllActive &&
                  productSelection.selectedItemIds.size === data.length && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => productSelection.handleSelectAll(true)}
                    >
                      Select all {totalProducts} products
                    </Button>
                  )}

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleBulkExport()}
                  disabled={productSelection.totalSelected === 0}
                >
                  Export {productSelection.totalSelected} products
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={updateTaxCodeModal.show}
                >
                  Update tax code
                </Button>

                <Button
                  variant="destructive-secondary"
                  size="sm"
                  onClick={deleteBulkModal.show}
                >
                  Delete
                </Button>
              </div>
            </div>
          ),
        }}
        tableViewConfig={{
          isEnabled: true,
          defaultView: 'regular',
        }}
        emptyStateConfig={getEmptyStateConfig()}
      />
    </SalesTaxSectionLayout>
  );
};

export default ProductsTableSection;
